import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaPlayCircle,
  FaGraduationCap,
  FaLock,
  FaSpinner,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

type Quiz = {
  quizId: string | number;
  title: string;
  chapter: string;
  deadline?: string;
  completed?: boolean;
  score?: number;
};

type QuizQuestion = {
  id: string | number;
  questionText: string;
  options: string[];
  correctAnswer?: string;
  explanation?: string;
};

const normalizeToArray = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.content)) return raw.content;
  if (Array.isArray(raw?.quizzes)) return raw.quizzes;
  if (raw && typeof raw === "object") return Object.values(raw);
  return [];
};

const StudentQuiz: React.FC = () => {
  const { student } = useAuth();

  const isInactive = React.useMemo(() => {
    const s: any = student || {};
    if (typeof s.isActiveStudent === "boolean")
      return s.isActiveStudent === false;
    if (typeof s.is_active_student === "boolean")
      return s.is_active_student === false;
    return false;
  }, [student]);

  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Quiz modal state
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<Quiz | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [userAnswers, setUserAnswers] = useState<
    Record<string | number, string>
  >({});
  const [submissionResult, setSubmissionResult] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ CRITICAL: Add refs for synchronous submission tracking
  const submittingRef = useRef(false);
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (isInactive) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const qRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/student/my-batch/quizzes`,
          { credentials: "include" }
        );
        if (qRes.ok) {
          const raw = await qRes.json().catch(() => []);
          const arr = normalizeToArray(raw).map(
            (q: any, idx: number): Quiz => ({
              quizId: q?.quizId ?? q?.id ?? `q-${idx}`,
              title: q?.title ?? q?.name ?? `Quiz #${idx + 1}`,
              chapter: q?.chapter ?? q?.topic ?? "N/A",
              deadline: q?.deadline ?? q?.dueDate ?? q?.endDate,
              completed: !!q?.completed,
              score: typeof q?.score === "number" ? q.score : undefined,
            })
          );
          setQuizzes(arr);
        } else {
          setQuizzes([]);
        }
      } catch (e: any) {
        setError(e.message || "Failed to load quizzes");
        setQuizzes([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isInactive]);

  const getQuizStatus = (quiz: Quiz) => {
    if (quiz.completed) return "completed";
    if (quiz.deadline && new Date(quiz.deadline) < new Date()) return "missed";
    return "pending";
  };

  const handleTakeQuiz = async (quiz: Quiz) => {
    if (!quiz || !quiz.quizId) {
      alert("Quiz information is not available.");
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/quizzes/students/${quiz.quizId}/questions`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to fetch quiz questions");
      const questionsRaw = await res.json();
      const questions: QuizQuestion[] = normalizeToArray(questionsRaw).map(
        (q: any, i: number) => ({
          id: q?.id ?? `qq-${i}`,
          questionText: q?.questionText ?? q?.question ?? `Question ${i + 1}`,
          options: Array.isArray(q?.options)
            ? q.options
            : typeof q?.options === "string"
            ? q.options.split("|").map((s: string) => s.trim())
            : [],
          correctAnswer: q?.correctAnswer,
          explanation: q?.explanation,
        })
      );

      setActiveQuiz(quiz);
      setQuizQuestions(questions);
      setUserAnswers({});
      setSubmissionResult(null);
      setIsSubmitting(false);

      // ✅ CRITICAL: Reset refs when opening new quiz
      submittingRef.current = false;
      hasSubmittedRef.current = false;

      setShowQuizModal(true);
    } catch (e) {
      alert("Could not load quiz questions.");
    }
  };

  const handleSubmitQuiz = async () => {
    // ✅ CRITICAL: Check refs first (synchronous check)
    if (submittingRef.current || hasSubmittedRef.current) {
      console.log("Submission already in progress or completed");
      return;
    }

    if (!activeQuiz) {
      alert("No active quiz to submit.");
      return;
    }
    if (!student) {
      alert("Student information is not available.");
      return;
    }

    // ✅ CRITICAL: Set refs immediately before any async operations
    submittingRef.current = true;
    hasSubmittedRef.current = true;
    setIsSubmitting(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/quizzes/students/${activeQuiz.quizId}/submit?studentId=${student.studentId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(userAnswers),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));

        // ✅ Check if it's a duplicate submission error
        if (
          errorData.message?.includes("already submitted") ||
          errorData.error?.includes("already submitted")
        ) {
          alert("⚠️ Quiz already submitted!");
          // Keep submitted state and show result
          const submission = { score: 0, answers: userAnswers };
          setSubmissionResult(submission);
          return;
        }

        throw new Error(errorData.message || "Failed to submit quiz");
      }

      const submission = await res.json();
      setSubmissionResult(submission);

      setQuizzes((prev) =>
        prev.map((q) =>
          q.quizId === activeQuiz.quizId
            ? { ...q, completed: true, score: submission.score }
            : q
        )
      );
    } catch (e: any) {
      console.error("Quiz submission error:", e);

      // ✅ Only reset if it's NOT an "already submitted" error
      if (!e.message?.includes("already submitted")) {
        submittingRef.current = false;
        hasSubmittedRef.current = false;
        setIsSubmitting(false);
        alert(e.message || "Could not submit quiz.");
      } else {
        alert("⚠️ This quiz has already been submitted.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseQuizModal = () => {
    setShowQuizModal(false);
    setActiveQuiz(null);
    setQuizQuestions([]);
    setUserAnswers({});
    setSubmissionResult(null);
    setIsSubmitting(false);

    // ✅ Reset refs when closing modal
    submittingRef.current = false;
    hasSubmittedRef.current = false;
  };

  // ✅ Check if all questions are answered
  const allQuestionsAnswered =
    quizQuestions.length > 0 &&
    Object.keys(userAnswers).length === quizQuestions.length;

  // ✅ Determine if submit button should be disabled
  const isSubmitDisabled =
    !allQuestionsAnswered ||
    isSubmitting ||
    submittingRef.current ||
    hasSubmittedRef.current;

  // Inactive lock screen
  if (isInactive) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaGraduationCap />}
          title="Manage Quizzes"
          breadcrumb={[{ label: "Home", to: "/" }, { label: "Quizzes" }]}
        />

        <main className="max-w-3xl mx-auto px-6 py-10">
          <div className="border border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50 text-rose-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <FaLock className="mt-0.5" />
              <div>
                <h2 className="font-semibold mb-1">Access Locked</h2>
                <p className="text-sm">
                  Your account is currently inactive. You can't access quizzes.
                  Please clear dues to reactivate your account.
                </p>
                <div className="mt-4 flex gap-3">
                  <Link
                    to="/student/student-dashboard"
                    className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 text-sm transition-colors"
                  >
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaGraduationCap />}
        title="Manage Quizzes"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Quizzes" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Loading/Error States */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your quizzes...</p>
          </div>
        ) : error ? (
          <div className="bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 text-rose-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <FaTimesCircle className="text-rose-600" />
              <h3 className="font-semibold">Error Loading Data</h3>
            </div>
            <p className="text-sm">{error}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <FaGraduationCap />
                Your Quiz & Results
              </h3>
              <p className="text-cyan-100 text-sm mt-1">
                Complete quizzes before the deadline
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {!Array.isArray(quizzes) || quizzes.length === 0 ? (
                <div className="text-center py-12">
                  <FaGraduationCap className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <h4 className="text-lg font-medium text-gray-700 mb-2">
                    No Quizzes Available
                  </h4>
                  <p className="text-gray-500 text-sm">
                    No quizzes have been assigned yet.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                          Quiz
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                          Chapter
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                          Deadline
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                          Status
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                          Score
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {quizzes.map((quiz, index) => {
                        const status = getQuizStatus(quiz);
                        return (
                          <tr
                            key={quiz.quizId}
                            className={`border-b border-gray-100 ${
                              index % 2 === 1 ? "bg-gray-50" : "bg-white"
                            } hover:bg-blue-50 transition-colors`}
                          >
                            <td className="py-4 px-4">
                              <div className="font-medium text-gray-900">
                                {quiz.title}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-gray-600">
                              {quiz.chapter}
                            </td>
                            <td className="py-4 px-4 text-gray-600 text-sm">
                              {quiz.deadline ? (
                                <div className="flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1 font-medium">
                                    <FaClock className="text-xs" />
                                    {new Date(quiz.deadline).toLocaleDateString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      }
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 ml-4">
                                    {new Date(quiz.deadline).toLocaleTimeString(
                                      "en-US",
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )}
                                  </div>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {status === "completed" && (
                                <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full text-xs font-semibold border border-emerald-200">
                                  <FaCheckCircle className="text-emerald-500" />
                                  Completed
                                </span>
                              )}
                              {status === "pending" && (
                                <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-3 py-1 rounded-full text-xs font-semibold border border-amber-200">
                                  <FaClock className="text-amber-500" />
                                  Pending
                                </span>
                              )}
                              {status === "missed" && (
                                <span className="inline-flex items-center gap-1.5 text-rose-700 bg-rose-50 px-3 py-1 rounded-full text-xs font-semibold border border-rose-200">
                                  <FaTimesCircle className="text-rose-500" />
                                  Missed
                                </span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {quiz.score !== undefined ? (
                                <span className="font-semibold text-gray-900">
                                  {quiz.score}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              <button
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm ${
                                  status === "pending"
                                    ? "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md"
                                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                }`}
                                onClick={() => handleTakeQuiz(quiz)}
                                disabled={status !== "pending"}
                                title={
                                  status === "completed"
                                    ? "Already completed"
                                    : status === "missed"
                                    ? "Deadline missed"
                                    : "Take Quiz"
                                }
                              >
                                <FaPlayCircle />
                                Take Quiz
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Quiz Modal */}
      {showQuizModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FaGraduationCap />
                  {activeQuiz?.title}
                </h2>
                <p className="text-cyan-100 text-sm mt-1">
                  Chapter: {activeQuiz?.chapter}
                </p>
              </div>
              <button
                className="text-white hover:text-gray-200 text-2xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCloseQuizModal}
                disabled={isSubmitting || submittingRef.current}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
              {!submissionResult ? (
                <>
                  {/* ✅ Warning about single submission */}
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700 font-semibold">
                          ⚠️ You can only submit once - review your answers
                          carefully!
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {quizQuestions.map((q, idx) => (
                      <div
                        key={`${q.id}-${idx}`}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="font-semibold mb-4 text-gray-900">
                          {idx + 1}. {q.questionText}
                        </div>
                        <div className="space-y-2">
                          {q.options?.map((opt, optIdx) => (
                            <label
                              key={`${opt}-${optIdx}`}
                              className={`flex items-center gap-3 p-2 rounded transition-colors ${
                                isSubmitting ||
                                submittingRef.current ||
                                hasSubmittedRef.current
                                  ? "cursor-not-allowed opacity-60"
                                  : "cursor-pointer hover:bg-white"
                              }`}
                            >
                              <input
                                type="radio"
                                name={String(q.id)}
                                value={opt}
                                checked={userAnswers[q.id] === opt}
                                onChange={() =>
                                  setUserAnswers((prev: any) => ({
                                    ...prev,
                                    [q.id]: opt,
                                  }))
                                }
                                disabled={
                                  isSubmitting ||
                                  submittingRef.current ||
                                  hasSubmittedRef.current
                                }
                                className="w-4 h-4 text-[#009fe3] focus:ring-[#009fe3] disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <span className="text-gray-700">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-col gap-3">
                    {/* Progress indicator */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Progress:</span>
                        <span className="font-semibold">
                          {Object.keys(userAnswers).length} /{" "}
                          {quizQuestions.length} answered
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#009fe3] h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${
                              (Object.keys(userAnswers).length /
                                quizQuestions.length) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                    </div>

                    <button
                      className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md flex items-center justify-center gap-2 ${
                        isSubmitDisabled
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                          : "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-lg"
                      }`}
                      onClick={handleSubmitQuiz}
                      disabled={isSubmitDisabled}
                    >
                      {isSubmitting || submittingRef.current ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Quiz"
                      )}
                    </button>

                    {!allQuestionsAnswered && (
                      <p className="text-sm text-red-600 text-center">
                        Please answer all questions before submitting.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Results Header */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-6 py-4">
                      <FaCheckCircle className="text-emerald-600 text-2xl" />
                      <div>
                        <div className="text-lg font-bold text-emerald-800">
                          Quiz Completed!
                        </div>
                        <div className="text-emerald-700">
                          Your Score: {submissionResult.score} /{" "}
                          {quizQuestions.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Results Details */}
                  <div className="space-y-4">
                    {quizQuestions.map((q, idx) => {
                      const answersObj = submissionResult.answers || {};
                      const userAnswer = answersObj[q.id];
                      const isCorrect = userAnswer === q.correctAnswer;
                      return (
                        <div
                          key={`${q.id}-${idx}`}
                          className={`p-4 rounded-lg border ${
                            isCorrect
                              ? "bg-emerald-50 border-emerald-200"
                              : "bg-rose-50 border-rose-200"
                          }`}
                        >
                          <div className="font-semibold mb-2 text-gray-900">
                            {idx + 1}. {q.questionText}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium">Your answer: </span>
                              <span
                                className={
                                  isCorrect
                                    ? "text-emerald-700 font-medium"
                                    : "text-rose-700 font-medium"
                                }
                              >
                                {String(userAnswer ?? "-")}
                              </span>
                            </div>
                            {!isCorrect && (
                              <>
                                <div>
                                  <span className="font-medium">
                                    Correct answer:{" "}
                                  </span>
                                  <span className="text-emerald-700 font-medium">
                                    {q.correctAnswer}
                                  </span>
                                </div>
                                {q.explanation && (
                                  <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-2">
                                    <span className="font-medium text-blue-800">
                                      Explanation:{" "}
                                    </span>
                                    <span className="text-blue-700">
                                      {q.explanation}
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold shadow-md"
                      onClick={handleCloseQuizModal}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentQuiz;
